@if (edit) {
    <clp-toolbar [title]="('subscription.update.title' | translate)"></clp-toolbar>
}
@else {
    <clp-toolbar [title]="('subscription.create.title' | translate)"></clp-toolbar>
}

<div class="px-4 py-3">
  <div class="mx-auto w-full max-w-4xl">
    <form class="space-y-4" (ngSubmit)="changeSubscription(subscriptionForm.value)" [formGroup]="subscriptionForm" novalidate>
      <input type="hidden" formControlName="id">

      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="text-base font-semibold text-slate-100">{{ 'subscription.generalData' | translate }}</h3>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div class="md:col-span-6">
              <label for="name" class="clp-label">{{ 'subscription.name' | translate }}</label>
              <input id="name" type="text" class="clp-input" formControlName="name" required>
              @if (subscriptionForm.controls['name'].invalid && subscriptionForm.controls['name'].touched) {
                <div class="clp-help">{{ 'subscription.create.invalidName' | translate }}</div>
              }
            </div>

            <div class="md:col-span-3">
              <label for="currency" class="clp-label">{{ 'subscription.currency' | translate }}</label>
              <select id="currency" class="clp-select" formControlName="currency" required>
                <option value="USD">{{ 'general.currency.usd' | translate }}</option>
                <option value="EUR">{{ 'general.currency.eur' | translate }}</option>
                <option value="BRL">{{ 'general.currency.brl' | translate }}</option>
              </select>
              @if (subscriptionForm.controls['currency'].invalid && subscriptionForm.controls['currency'].touched) {
                <div class="clp-help">{{ 'subscription.create.invalidCurrency' | translate }}</div>
              }
            </div>

            <div class="md:col-span-3">
              <label for="visibility" class="clp-label">{{ 'subscription.visibilityLabel' | translate }}</label>
              <select id="visibility" class="clp-select" formControlName="visibility" required>
                @for (visibility of visibilities; track visibility) {
                  <option [value]="visibility.value">{{ visibility.label | translate }}</option>
                }
              </select>
              @if (subscriptionForm.controls['visibility'].invalid && subscriptionForm.controls['visibility'].touched) {
                <div class="clp-help">{{ 'subscription.create.invalidVisibility' | translate }}</div>
              }
            </div>
          </div>

          <div>
            <label for="description" class="clp-label">{{ 'subscription.description' | translate }}</label>
            <textarea id="description" class="clp-textarea" formControlName="description" rows="4"></textarea>
            @if (subscriptionForm.controls['description'].invalid && subscriptionForm.controls['description'].touched) {
              <div class="clp-help">{{ 'subscription.create.invalidDescription' | translate }}</div>
            }
          </div>

          <div class="max-w-md">
            <label for="coverUrl" class="clp-label">{{ 'subscription.coverUrl' | translate }}</label>
            <clp-image-upload
              (imageFile)="coverChanged($event)"
              (deleteImageFile)="deleteCover($event)"
              [imageDataUrl]="imageUrl">
            </clp-image-upload>
          </div>
        </div>
      </section>

      <section class="clp-card">
        <div class="clp-card-body space-y-4">
          <h3 class="text-base font-semibold text-slate-100">{{ 'subscription.billingData' | translate }}</h3>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
            <div class="md:col-span-4">
              <label for="billingType" class="clp-label">{{ 'subscription.billingTypeLabel' | translate }}</label>
              <select id="billingType" class="clp-select" formControlName="billingType" required>
                @for (billingType of billingTypes; track billingType) {
                  <option [value]="billingType.value">{{ billingType.label | translate }}</option>
                }
              </select>
              @if (subscriptionForm.controls['billingType'].invalid && subscriptionForm.controls['billingType'].touched) {
                <div class="clp-help">{{ 'subscription.create.invalidBillingType' | translate }}</div>
              }
            </div>

            @if (isRecurring()) {
              <div class="md:col-span-4">
                <label for="recurringFee" class="clp-label">{{ 'subscription.recurringFee' | translate }}</label>
                <input id="recurringFee" type="number" class="clp-input" formControlName="recurringFee" min="0.01" step="0.01">
                @if (subscriptionForm.controls['recurringFee'].invalid && subscriptionForm.controls['recurringFee'].touched) {
                  <div class="clp-help">{{ 'subscription.create.invalidRecurringFee' | translate }}</div>
                }
              </div>

              <div class="md:col-span-4">
                <label for="recurringInterval" class="clp-label">{{ 'subscription.recurringIntervalLabel' | translate }}</label>
                <select id="recurringInterval" class="clp-select" formControlName="recurringInterval">
                  @for (recurringInterval of recurringIntervals; track recurringInterval) {
                    <option [value]="recurringInterval.value">{{ recurringInterval.label | translate }}</option>
                  }
                </select>
                @if (subscriptionForm.controls['recurringInterval'].invalid && subscriptionForm.controls['recurringInterval'].touched) {
                  <div class="clp-help">{{ 'subscription.create.invalidRecurringInterval' | translate }}</div>
                }
              </div>

              <div class="md:col-span-6">
                <label for="recurringIntervalCount" class="clp-label">{{ 'subscription.recurringIntervalCount' | translate }}</label>
                <input id="recurringIntervalCount" type="number" class="clp-input" formControlName="recurringIntervalCount" min="1" max="365">
                <p class="mt-1 text-xs text-slate-400">{{ 'subscription.recurringIntervalCountHelp' | translate }}</p>
                @if (subscriptionForm.controls['recurringIntervalCount'].invalid && subscriptionForm.controls['recurringIntervalCount'].touched) {
                  <div class="clp-help">{{ 'subscription.create.invalidRecurringIntervalCount' | translate }}</div>
                }
              </div>

              <div class="md:col-span-6">
                <label for="trialDays" class="clp-label">{{ 'subscription.trialDays' | translate }}</label>
                <input id="trialDays" type="number" class="clp-input" formControlName="trialDays" min="0" max="365">
                @if (subscriptionForm.controls['trialDays'].invalid && subscriptionForm.controls['trialDays'].touched) {
                  <div class="clp-help">{{ 'subscription.create.invalidTrialDays' | translate }}</div>
                }
              </div>
            } @else {
              <div class="md:col-span-4">
                <label for="oneTimeFee" class="clp-label">{{ 'subscription.oneTimeFee' | translate }}</label>
                <input id="oneTimeFee" type="number" class="clp-input" formControlName="oneTimeFee" min="0.01" step="0.01">
                @if (subscriptionForm.controls['oneTimeFee'].invalid && subscriptionForm.controls['oneTimeFee'].touched) {
                  <div class="clp-help">{{ 'subscription.create.invalidOneTimeFee' | translate }}</div>
                }
              </div>
            }
          </div>
        </div>
      </section>

      <div class="flex justify-end gap-2">
        <a class="clp-btn clp-btn-secondary" [routerLink]="['/listSubscriptions']">{{ 'general.cancel' | translate }}</a>
        <button class="clp-btn clp-btn-warning" type="submit">{{ 'general.save' | translate }}</button>
      </div>
    </form>
  </div>
</div>
